import React, { useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { ScrollArea } from "@shadcn-in-nx/ui/scroll-area";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import CodeScanHeader from "./CodeScanHeader";
import CodeScanSummary from "./CodeScanSummary";
import CodeScanExplainer from "./CodeScanExplainer";
import PlanRestrictionAlert from "./PlanRestrictionAlert";
import CodeScanStatusDisplay from "./CodeScanStatusDisplay";
import CodeScanHistorySelect from "./CodeScanHistorySelect";
import useCodeScanData from "./useCodeScanData";

function CodeScanningPanel({ stepId, selectedEnvironment }) {
    const { t } = useTranslation();
    const { isFeatureEnabled } = useAuth();
    const hasAccess = isFeatureEnabled("codeScanning");
    const { call: triggerScan } = useBluelagoon({ method: "triggerCodeScan" });
    const { call: updateFinding } = useBluelagoon({ method: "updateCodeScanFinding" });

    const {
        scanResult, setScanResult, findings, setFindings,
        isScanning, setIsScanning, scanTimedOut, setScanTimedOut,
        scanHistory, fetchResults, loadScanById,
    } = useCodeScanData(stepId, hasAccess);

    const handleRunScan = useCallback(async () => {
        if (!stepId) return;
        setIsScanning(true);
        setFindings([]);
        setScanTimedOut(false);
        try {
            const res = await triggerScan({ stepId, environmentName: selectedEnvironment });
            const data = res?.data?.data || res?.data || res;
            if (data && data.id) {
                setScanResult(data);
            }
            toast.success(t("appPreview.scanStarted"));
        } catch (err) {
            setIsScanning(false);
            const msg = err?.message || err?.error?.message || err?.error || t("appPreview.scanFailed");
            toast.error(typeof msg === "string" ? msg : t("appPreview.scanFailed"));
        }
    }, [stepId, selectedEnvironment, triggerScan, t, setIsScanning, setFindings, setScanTimedOut, setScanResult]);

    const handleUpdateStatus = useCallback(async (findingId, status) => {
        try {
            await updateFinding({ findingId, status });
            setFindings((prev) => prev.map((f) => (f.id === findingId ? { ...f, status } : f)));
        } catch (err) {
            const msg = err?.message || err?.error?.message || err?.error || "Failed to update finding";
            toast.error(typeof msg === "string" ? msg : "Failed to update finding");
        }
    }, [updateFinding, setFindings]);

    return (
        <div className="flex flex-col h-full">
            <CodeScanHeader scanResult={hasAccess ? scanResult : null} isScanning={isScanning} onRunScan={handleRunScan} disabled={!hasAccess} />
            {!hasAccess ? (
                <ScrollArea className="flex-1">
                    <PlanRestrictionAlert
                        titleKey="appPreview.upgradeRequired"
                        titleFallback="Upgrade Required"
                        descriptionKey="appPreview.codeScanUpgradeMessage"
                        descriptionFallback="Code Scanning is available on the Visionary plan and above."
                    />
                    <CodeScanExplainer />
                </ScrollArea>
            ) : (
                <ScrollArea className="flex-1">
                    {scanHistory.length > 1 && (
                        <CodeScanHistorySelect
                            scanHistory={scanHistory}
                            selectedScanId={scanResult?.id}
                            onSelectScan={loadScanById}
                        />
                    )}
                    <CodeScanSummary scanResult={scanResult} />
                    <CodeScanStatusDisplay
                        scanResult={scanResult}
                        isScanning={isScanning}
                        scanTimedOut={scanTimedOut}
                        findings={findings}
                        onUpdateStatus={handleUpdateStatus}
                        onRefresh={fetchResults}
                    />
                </ScrollArea>
            )}
        </div>
    );
}

export default React.memo(CodeScanningPanel);
